import { useQuery } from '@tanstack/react-query';

import { useAccessToken } from '@app/auth';
import { fetchApi } from '@app/hooks/api';

export interface ResolvedFlag {
  enabled: boolean;
  value?: string;
}

export type ResolvedFlags = Record<string, ResolvedFlag>;

const NO_FLAGS: ResolvedFlags = {};

/**
 * Admin-managed feature flags, evaluated server-side for the signed-in user (GET /api/app-config/me).
 * Until they load (or if the call fails) every flag reads as off, and the server still enforces its own rules.
 */
export function useFeatureFlags(): { flags: ResolvedFlags; isLoading: boolean } {
  const getToken = useAccessToken();
  const query = useQuery<{ flags: ResolvedFlags }>({
    queryKey: ['appConfig', 'me'],
    queryFn: () => fetchApi<{ flags: ResolvedFlags }>('/api/app-config/me', getToken),
    staleTime: 60 * 1000,
    retry: 1,
  });
  return { flags: query.data?.flags ?? NO_FLAGS, isLoading: query.isLoading };
}

export function useFeatureFlag(key: string): boolean {
  return useFeatureFlags().flags[key]?.enabled === true;
}
